import { Component, OnDestroy, OnInit, inject, ChangeDetectorRef, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { KeycloakService, KeycloakEventType } from 'keycloak-angular';
import { Subscription } from 'rxjs';
import { SubHeaderComponent } from '../../../shared/components/sub-header/sub-header.component';
import {
  AssetFilterComponent,
  FilterConfig,
  FilterOption,
} from '../../../shared/components/asset-filter/asset-filter.component';
import {
  AssetNavbarComponent,
  AssetNavbarViewMode,
  ListingSortOptionId,
} from '../../../shared/components/asset-navbar/asset-navbar.component';
import { LoginModalComponent } from '../../../shared/components/login-modal/login-modal.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import { SkeletonComponent } from '../../../shared/components/skeleton/skeleton.component';
import { ModelCard, ModelCardData } from '../model-card/model-card';
import { CatalogueApiService, SearchCriteria, UserInteractionApiService } from '@angular-workspace/services';
import { FormatService } from '../../../shared/services/format.service';
import { HelperService } from '../../../shared/services/helper.service';

@Component({
  selector: 'app-model-home',
  standalone: true,
  imports: [
    CommonModule,
    SubHeaderComponent,
    AssetFilterComponent,
    AssetNavbarComponent,
    ModelCard,
    LoginModalComponent,
    FooterComponent,
    SkeletonComponent,
  ],
  templateUrl: './model-home.html',
  styleUrl: './model-home.scss',
})
export class ModelHome implements OnInit, OnDestroy {
  private catalogueApiService = inject(CatalogueApiService);
  private userInteractionApiService = inject(UserInteractionApiService);
  private formatService = inject(FormatService);
  private helperService = inject(HelperService);
  private keycloak = inject(KeycloakService);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);

  private keycloakSubscription?: Subscription;
  private interactionRefreshInFlight = false;

  readonly isLoading = signal(false);

  searchQuery = '';
  sortOption: ListingSortOptionId = 'new-to-old';
  viewMode: AssetNavbarViewMode = 'grid';

  models: ModelCardData[] = [];
  totalCount = 0;
  currentPage = 1;
  totalPages = 0;
  itemsPerPage = 6;

  bookmarksActive = false;
  likeActive = false;
  bookmarkedItemIds: string[] = [];
  likedItemIds: string[] = [];
  bookmarksTotalCount = 0;
  likesTotalCount = 0;
  filteredAssetsFromSearch: ModelCardData[] = [];
  filteredTotalPages = 1;

  isLoggedIn = false;
  showLoginModal = false;

  filterConfig: FilterConfig = {
    activeFilterCount: 0,
    sections: [
      {
        id: 'organisation',
        title: 'ORGANISATION',
        type: 'checkbox',
        isExpanded: false,
        options: [],
      },
      {
        id: 'organisation-type',
        title: 'ORGANISATION TYPE',
        type: 'checkbox',
        isExpanded: false,
        options: [],
      },
      {
        id: 'model-type',
        title: 'MODEL TYPE',
        type: 'checkbox',
        isExpanded: false,
        options: [],
      },
      {
        id: 'file-format',
        title: 'FILE FORMAT',
        type: 'checkbox',
        isExpanded: false,
        options: [],
      },
      {
        id: 'permissions',
        title: 'PERMISSIONS',
        type: 'radio',
        isExpanded: false,
        options: [
          { id: 'OPEN', label: 'Public', checked: false },
          { id: 'RESTRICTED', label: 'Restricted', checked: false },
          { id: 'PII', label: 'Personal', checked: false },
        ],
      },
      {
        id: 'last-updated',
        title: 'LAST UPDATED AT',
        type: 'radio',
        isExpanded: false,
        options: [
          { id: '1-day', label: '1 Day ago', checked: false },
          { id: '1-week', label: '1 week ago', checked: false },
          { id: '1-month', label: '1 month ago', checked: false },
          { id: '1-year', label: '1 year ago', checked: false },
        ],
      },
    ],
  };

  get showingFrom(): number {
    const tc = this.effectiveTotalCount;
    if (tc === 0) return 0;
    return (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  get showingTo(): number {
    const tc = this.effectiveTotalCount;
    if (tc === 0) return 0;
    return Math.min(this.currentPage * this.itemsPerPage, tc);
  }

  get effectiveTotalCount(): number {
    if (this.bookmarksActive) return this.bookmarksTotalCount;
    if (this.likeActive) return this.likesTotalCount;
    return this.totalCount;
  }

  get effectiveTotalPages(): number {
    if (this.bookmarksActive || this.likeActive) {
      return Math.max(1, this.filteredTotalPages);
    }
    return Math.max(1, this.totalPages);
  }

  get displayedModels(): ModelCardData[] {
    if (this.bookmarksActive || this.likeActive) {
      return this.filteredAssetsFromSearch;
    }
    return this.models;
  }

  get skeletonCardIndices(): number[] {
    return Array.from({ length: this.itemsPerPage }, (_, i) => i);
  }

  get searchPlaceholderDynamic(): string {
    const n = this.totalCount;
    return n > 0 ? `Search across ${n} models…` : 'Search models…';
  }

  ngOnInit(): void {
    void this.checkLoginStatus();
    this.keycloakSubscription = this.keycloak.keycloakEvents$.subscribe(async (event: unknown) => {
      const keycloakEvent = event as { type: KeycloakEventType };
      if (keycloakEvent.type === KeycloakEventType.OnAuthSuccess) {
        this.isLoggedIn = true;
      } else if (
        keycloakEvent.type === KeycloakEventType.OnAuthRefreshSuccess ||
        keycloakEvent.type === KeycloakEventType.OnAuthLogout
      ) {
        await this.checkLoginStatus();
      }
      this.cdr.markForCheck();
    });

    this.route.queryParams.subscribe((params) => {
      if (params['search']) {
        this.searchQuery = params['search'];
      }
    });

    void this.loadFilterOptions();
    void this.loadModels();
  }

  ngOnDestroy(): void {
    this.keycloakSubscription?.unsubscribe();
  }

  async checkLoginStatus(): Promise<void> {
    try {
      this.isLoggedIn = await this.keycloak.isLoggedIn();
    } catch {
      this.isLoggedIn = false;
    }
  }

  onViewModeChange(mode: AssetNavbarViewMode): void {
    this.viewMode = mode;
  }

  onSearchChange(query: string): void {
    this.searchQuery = query;
    const trimmed = query.trim();
    if (trimmed.length >= 3) {
      this.currentPage = 1;
      void this.loadData();
    } else if (trimmed.length === 0) {
      this.currentPage = 1;
      void this.loadData();
    }
  }

  onSortChange(opt: ListingSortOptionId): void {
    this.sortOption = opt;
    this.currentPage = 1;
    void this.loadData();
  }

  onFilterChange(config: FilterConfig): void {
    this.filterConfig = config;
    this.updateActiveFilterCount();
    this.currentPage = 1;
    void this.loadData();
  }

  onResetFilters(): void {
    this.filterConfig.sections.forEach((section) => {
      if (section.options) {
        section.options.forEach((option) => {
          option.checked = false;
        });
      }
    });
    this.updateActiveFilterCount();
    this.currentPage = 1;
    void this.loadData();
  }

  onPageChange(page: number): void {
    const max = this.effectiveTotalPages;
    if (page < 1 || page > max) return;
    this.currentPage = page;
    void this.loadData();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onBookmarksNavbarClick(): void {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      this.cdr.markForCheck();
      return;
    }
    void this.toggleBookmarksFilter();
  }

  onLikesNavbarClick(): void {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      this.cdr.markForCheck();
      return;
    }
    void this.toggleLikesFilter();
  }

  onLoginModalVisibleChange(visible: boolean): void {
    this.showLoginModal = visible;
  }

  onLoginRequiredFromCard(): void {
    this.showLoginModal = true;
    this.cdr.markForCheck();
  }

  onBookmarkChangedFromCard(assetId: string, isBookmarked: boolean): void {
    if (isBookmarked) {
      if (!this.bookmarkedItemIds.includes(assetId)) this.bookmarkedItemIds.push(assetId);
    } else {
      this.bookmarkedItemIds = this.bookmarkedItemIds.filter((id) => id !== assetId);
    }
  }

  onLikeChangedFromCard(assetId: string, isLiked: boolean): void {
    if (isLiked) {
      if (!this.likedItemIds.includes(assetId)) this.likedItemIds.push(assetId);
    } else {
      this.likedItemIds = this.likedItemIds.filter((id) => id !== assetId);
    }
  }

  isAssetBookmarked(id: string | undefined): boolean {
    return !!id && this.isLoggedIn && this.bookmarkedItemIds.includes(id);
  }

  isAssetLiked(id: string | undefined): boolean {
    return !!id && this.isLoggedIn && this.likedItemIds.includes(id);
  }

  get paginationItems(): (number | 'ellipsis')[] {
    const total = this.effectiveTotalPages;
    const cur = this.currentPage;
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const items: (number | 'ellipsis')[] = [];
    const add = (x: number | 'ellipsis') => items.push(x);

    const windowStart = Math.max(2, cur - 1);
    const windowEnd = Math.min(total - 1, cur + 1);

    add(1);
    if (windowStart > 2) add('ellipsis');
    for (let p = windowStart; p <= windowEnd; p++) {
      if (p !== 1 && p !== total) add(p);
    }
    if (windowEnd < total - 1) add('ellipsis');
    if (total > 1) add(total);

    return items;
  }

  private async loadData(): Promise<void> {
    if (this.bookmarksActive || this.likeActive) {
      await this.refreshInteractionFilteredAssets();
    } else {
      await this.loadModels();
    }
  }

  private async loadModels(): Promise<void> {
    this.isLoading.set(true);
    try {
      const searchCriteria = this.buildSearchCriteriaFromFilters();
      const sortString = this.getSortStringFromOption(this.sortOption);
      const q = this.getSearchQueryForApi();

      const response = await this.catalogueApiService.searchAllAssets(
        q,
        ['Model'],
        [],
        this.currentPage,
        this.itemsPerPage,
        sortString,
        searchCriteria
      );

      const items = this.extractItemsFromResponse(response);
      this.models = items.map((item: unknown) => this.mapApiItemToModelCard(item));
      this.updatePaginationFromResponse(response);
      this.cdr.markForCheck();
    } catch (error) {
      console.error('Failed to load models:', error);
      this.models = [];
      this.totalCount = 0;
      this.totalPages = 1;
      this.cdr.markForCheck();
    } finally {
      this.isLoading.set(false);
      this.cdr.markForCheck();
    }
  }

  private getSearchQueryForApi(): string {
    const t = (this.searchQuery || '').trim();
    if (t.length >= 3) return t;
    return '';
  }

  private async toggleBookmarksFilter(): Promise<void> {
    if (this.bookmarksActive) {
      this.bookmarksActive = false;
      this.bookmarksTotalCount = 0;
      this.bookmarkedItemIds = [];
      this.filteredAssetsFromSearch = [];
      this.filteredTotalPages = 1;
      this.currentPage = 1;
      await this.loadModels();
      this.cdr.markForCheck();
      return;
    }

    if (this.likeActive) {
      this.likeActive = false;
      this.likesTotalCount = 0;
      this.likedItemIds = [];
      this.filteredAssetsFromSearch = [];
      this.filteredTotalPages = 1;
    }

    this.bookmarksActive = true;
    this.currentPage = 1;
    this.filteredAssetsFromSearch = [];
    await this.refreshInteractionFilteredAssets();
  }

  private async toggleLikesFilter(): Promise<void> {
    if (this.likeActive) {
      this.likeActive = false;
      this.likesTotalCount = 0;
      this.likedItemIds = [];
      this.filteredAssetsFromSearch = [];
      this.filteredTotalPages = 1;
      this.currentPage = 1;
      await this.loadModels();
      this.cdr.markForCheck();
      return;
    }

    if (this.bookmarksActive) {
      this.bookmarksActive = false;
      this.bookmarksTotalCount = 0;
      this.bookmarkedItemIds = [];
      this.filteredAssetsFromSearch = [];
      this.filteredTotalPages = 1;
    }

    this.likeActive = true;
    this.currentPage = 1;
    this.filteredAssetsFromSearch = [];
    await this.refreshInteractionFilteredAssets();
  }

  private async refreshInteractionFilteredAssets(): Promise<void> {
    if (!this.isLoggedIn) return;
    if (this.interactionRefreshInFlight) return;

    this.interactionRefreshInFlight = true;
    this.isLoading.set(true);
    const actionType = this.bookmarksActive ? 'BOOKMARK' : 'LIKE';

    try {
      this.filteredAssetsFromSearch = [];
      this.filteredTotalPages = 1;

      const response = await this.userInteractionApiService.userInteractions({
        actionType: actionType as 'BOOKMARK' | 'LIKE',
        page: this.currentPage,
        size: this.itemsPerPage,
      });

      if (!response?.paginationInfo) {
        if (this.bookmarksActive) {
          this.bookmarksTotalCount = 0;
          this.bookmarkedItemIds = [];
        } else {
          this.likesTotalCount = 0;
          this.likedItemIds = [];
        }
        this.cdr.markForCheck();
        return;
      }

      const totalCount = response.paginationInfo.totalCount || 0;
      const paginationTotalPages = response.paginationInfo.totalPages;
      let rawItems: unknown[] = [];
      if (Array.isArray(response.result)) rawItems = response.result;
      else if (Array.isArray(response.results)) rawItems = response.results;
      else if (Array.isArray(response.data)) rawItems = response.data;

      if (this.bookmarksActive) {
        this.bookmarksTotalCount = totalCount;
      } else {
        this.likesTotalCount = totalCount;
      }

      if (rawItems.length === 0) {
        this.filteredAssetsFromSearch = [];
        this.filteredTotalPages = paginationTotalPages ?? 1;
        if (this.bookmarksActive) this.bookmarkedItemIds = [];
        else this.likedItemIds = [];
        this.cdr.markForCheck();
        return;
      }

      const assetIds: string[] = rawItems
        .map((item: unknown) => {
          const asset = this.getAssetFromInteractionItem(item);
          const itemRec = item as Record<string, unknown>;
          if (asset) {
            const a = asset as Record<string, unknown>;
            return String(
              a['id'] ?? a['itemId'] ?? a['assetId'] ?? itemRec['assetId'] ?? itemRec['itemId'] ?? itemRec['id'] ?? ''
            );
          }
          return String(itemRec['assetId'] ?? itemRec['itemId'] ?? itemRec['id'] ?? '');
        })
        .filter((id) => id.length > 0);

      if (this.bookmarksActive) this.bookmarkedItemIds = assetIds;
      else this.likedItemIds = assetIds;

      const assetsFromInteraction = rawItems
        .map((item) => this.getAssetFromInteractionItem(item))
        .filter((a) => a != null);

      if (assetsFromInteraction.length > 0) {
        this.filteredTotalPages =
          paginationTotalPages ??
          Math.max(1, Math.ceil((totalCount || assetsFromInteraction.length) / this.itemsPerPage));
        this.filteredAssetsFromSearch = assetsFromInteraction.map((item) =>
          this.mapApiItemToModelCard(item)
        );
      } else if (assetIds.length > 0) {
        const searchResponse = await this.catalogueApiService.searchAssetsByIds(
          assetIds,
          'Model',
          this.currentPage,
          this.itemsPerPage
        );
        const searchItems = this.extractItemsFromResponse(searchResponse);
        if (searchResponse?.paginationInfo?.totalPages !== undefined) {
          this.filteredTotalPages = searchResponse.paginationInfo.totalPages || 1;
        } else {
          const responseTotal =
            searchResponse?.paginationInfo?.totalCount || searchItems.length || 0;
          this.filteredTotalPages = Math.max(1, Math.ceil(responseTotal / this.itemsPerPage));
        }
        this.filteredAssetsFromSearch = searchItems.map((item: unknown) =>
          this.mapApiItemToModelCard(item)
        );
      }

      this.cdr.markForCheck();
    } catch (e) {
      console.error('Interaction filter failed:', e);
      this.filteredAssetsFromSearch = [];
      this.filteredTotalPages = 1;
    } finally {
      this.isLoading.set(false);
      this.interactionRefreshInFlight = false;
      this.cdr.markForCheck();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getAssetFromInteractionItem(item: any): any | null {
    if (!item || typeof item !== 'object') return null;
    const asset = item.assetDetails ?? item.item ?? item.asset ?? item;
    if (!asset || typeof asset !== 'object') return null;
    const hasId = asset.id != null || asset.itemId != null || asset.assetId != null;
    const hasName =
      (asset.name != null && asset.name !== '') ||
      (asset.label != null && asset.label !== '') ||
      (asset.title != null && asset.title !== '');
    if (hasId || hasName) return asset;
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private extractItemsFromResponse(response: any): any[] {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    if (Array.isArray(response.result)) return response.result;
    if (response.result && typeof response.result === 'object' && Array.isArray(response.result.results)) {
      return response.result.results;
    }
    if (Array.isArray(response.data)) return response.data;
    if (Array.isArray(response.items)) return response.items;
    return [];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mapApiItemToModelCard(item: any): ModelCardData {
    const id = String(item?.id ?? '');
    const title = String(item?.label ?? item?.name ?? 'Untitled');
    const organization = String(
      item?.provider ??
        item?.organisation ??
        item?.organization ??
        item?.provider_name ??
        item?.organizationName ??
        ''
    );
    const rawDesc =
      (item?.shortDescription as string) ??
      (item?.short_description as string) ??
      (item?.description as string) ??
      '';
    const shortDescription =
      typeof rawDesc === 'string' && rawDesc.trim()
        ? rawDesc.length > 120
          ? rawDesc.trim().slice(0, 120) + '…'
          : rawDesc.trim()
        : undefined;

    let industry = '';
    if (item?.industry) {
      if (Array.isArray(item.industry)) {
        industry = item.industry
          .filter((v: unknown) => v !== null && v !== undefined && v !== '')
          .join(', ');
      } else if (typeof item.industry === 'string' && item.industry.trim() !== '') {
        industry = item.industry.trim();
      }
    }

    const apiTags: string[] = [];
    if (Array.isArray(item?.tags)) {
      apiTags.push(...item.tags);
    }

    const modelType = String(item?.modelType ?? '').trim() || undefined;
    const language =
      String(item?.codeLanguage ?? item?.fileFormat ?? item?.format ?? '').trim() || undefined;

    const date = this.formatService.formatDateForModelCard(
      item?.itemCreatedAt ?? item?.createdAt ?? new Date()
    );

    const badge = this.helperService.extractAccessBadge(item);
    const badgeColor = this.helperService.getBadgeColorVariant(badge);

    const metricsData = item?.metrics || {};
    const metrics = {
      likes: metricsData.likes ?? item?.likeCount ?? item?.likes ?? 0,
      downloads: metricsData.downloads ?? item?.downloadCount ?? item?.downloads ?? 0,
      files: item?.fileCount ?? item?.files ?? 0,
      views: metricsData.views ?? item?.viewCount ?? item?.views ?? 0,
    };

    return {
      id,
      title,
      shortDescription,
      organization,
      industry: industry || undefined,
      tags: apiTags.slice(0, 3),
      modelType,
      language,
      date,
      badge,
      badgeColor,
      link: `/model/${id}`,
      metrics,
    };
  }

  private buildSearchCriteriaFromFilters(): SearchCriteria[] {
    const searchCriteria: SearchCriteria[] = [];

    for (const section of this.filterConfig.sections) {
      if (!section.options?.length) continue;
      const checked = section.options.filter((opt) => opt.checked);
      if (!checked.length) continue;

      const values = checked.map((opt) => opt.label);

      switch (section.id) {
        case 'file-format':
          searchCriteria.push({ searchType: 'term', field: 'fileFormat', values });
          break;
        case 'model-type':
          searchCriteria.push({ searchType: 'term', field: 'modelType', values });
          break;
        case 'permissions': {
          const apiVals = checked.map((opt) => {
            const fo = opt as FilterOption;
            if (fo.originalValue) return fo.originalValue;
            const lower = opt.label.toLowerCase();
            if (lower === 'public') return 'open';
            if (lower === 'restricted') return 'restricted';
            if (lower === 'personal') return 'pii';
            return lower;
          });
          searchCriteria.push({ searchType: 'term', field: 'accessPolicy', values: apiVals });
          break;
        }
        case 'last-updated': {
          const range = this.getDateRangeFromLastUpdated(checked);
          if (range) {
            searchCriteria.push({
              searchType: 'betweenTemporal',
              field: 'itemCreatedAt',
              values: [range.start, range.end],
            });
          }
          break;
        }
        case 'organisation':
          searchCriteria.push({ searchType: 'term', field: 'organization', values });
          break;
        case 'organisation-type':
          searchCriteria.push({ searchType: 'term', field: 'organizationType', values });
          break;
      }
    }

    return searchCriteria;
  }

  private getDateRangeFromLastUpdated(checked: FilterOption[]): { start: string; end: string } | null {
    if (!checked.length) return null;
    const now = new Date();
    let startDate: Date | null = null;
    const endDate = new Date(now);

    for (const option of checked) {
      let daysAgo = 0;
      switch (option.id) {
        case '1-day':
          daysAgo = 1;
          break;
        case '1-week':
          daysAgo = 7;
          break;
        case '1-month':
          daysAgo = 30;
          break;
        case '1-year':
          daysAgo = 365;
          break;
        default:
          break;
      }
      if (daysAgo > 0) {
        const optionStart = new Date(now);
        optionStart.setDate(optionStart.getDate() - daysAgo);
        if (!startDate || optionStart > startDate) startDate = optionStart;
      }
    }

    if (!startDate) return null;

    const formatDateWithTimezone = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      const offset = -date.getTimezoneOffset();
      const offsetHours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
      const offsetMinutes = String(Math.abs(offset) % 60).padStart(2, '0');
      const offsetSign = offset >= 0 ? '+' : '-';
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetSign}${offsetHours}${offsetMinutes}`;
    };

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    return { start: formatDateWithTimezone(startDate), end: formatDateWithTimezone(endDate) };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private updatePaginationFromResponse(response: any): void {
    const pi = response?.paginationInfo;
    if (pi) {
      if (pi.totalCount !== undefined) this.totalCount = pi.totalCount;
      if (pi.totalPages !== undefined) {
        this.totalPages = Math.max(1, pi.totalPages);
      } else if (pi.totalCount !== undefined) {
        this.totalPages = Math.max(1, Math.ceil(pi.totalCount / this.itemsPerPage));
      }
      if (pi.page !== undefined) this.currentPage = pi.page;
      return;
    }
    const result = response?.result;
    if (result?.totalCount !== undefined) {
      this.totalCount = result.totalCount;
      this.totalPages = Math.max(1, Math.ceil(result.totalCount / this.itemsPerPage));
    } else if (typeof response?.totalCount === 'number') {
      this.totalCount = response.totalCount;
      this.totalPages = Math.max(1, Math.ceil(response.totalCount / this.itemsPerPage));
    }
  }

  private getSortStringFromOption(option: string): string {
    switch (option) {
      case 'a-z':
        return 'name:asc;';
      case 'z-a':
        return 'name:desc;';
      case 'new-to-old':
        return 'itemCreatedAt:desc;';
      case 'old-to-new':
        return 'itemCreatedAt:asc;';
      default:
        return 'itemCreatedAt:desc;';
    }
  }

  private updateActiveFilterCount(): void {
    let count = 0;
    for (const section of this.filterConfig.sections) {
      for (const option of section.options ?? []) {
        if (option.checked) count += 1;
      }
    }
    this.filterConfig.activeFilterCount = count;
  }

  private async loadFilterOptions(): Promise<void> {
    const searchCriteria: SearchCriteria[] = [{ searchType: 'term', field: 'type', values: ['adex:AiModel'] }];
    const filterFields = [
      'organization',
      'fileFormat',
      'organizationType',
      'modelType',
      'accessPolicy',
    ];
    try {
      const response = await this.catalogueApiService.getFilterList(searchCriteria, filterFields);
      const apiData = response?.result ?? response?.results ?? response?.data ?? response;
      this.populateFilterSections(apiData);
      this.updateActiveFilterCount();
      this.cdr.markForCheck();
    } catch (e) {
      console.error('Failed to load filter options:', e);
    }
  }

  private populateFilterSections(apiData: unknown): void {
    const extractValues = (data: unknown, field: string): string[] => {
      if (!data || typeof data !== 'object') return [];
      const d = data as Record<string, unknown>;

      const getAtPath = (obj: Record<string, unknown>, path: string): unknown => {
        const parts = path.split('.');
        let v: unknown = obj;
        for (const p of parts) {
          v = (v as Record<string, unknown>)?.[p];
        }
        return v;
      };

      if (Array.isArray(d) && d.length > 0 && typeof d[0] === 'object') {
        const values: string[] = [];
        for (const item of d as Record<string, unknown>[]) {
          const raw = getAtPath(item, field);
          if (Array.isArray(raw)) {
            for (const v of raw) {
              if (v !== null && v !== undefined && v !== '') values.push(String(v));
            }
          } else if (raw !== null && raw !== undefined && raw !== '') {
            values.push(String(raw));
          }
        }
        return [...new Set(values)];
      }

      const raw = getAtPath(d as Record<string, unknown>, field);
      if (Array.isArray(raw)) {
        return [...new Set(raw.filter((v) => v !== null && v !== undefined && v !== '').map(String))];
      }
      if (raw !== null && raw !== undefined && raw !== '') return [String(raw)];
      return [];
    };

    const populateSection = (sectionId: string, values: string[]): void => {
      const section = this.filterConfig.sections.find((s) => s.id === sectionId);
      if (!section || !values.length) return;
      section.options = values.map((value) => ({ id: value, label: value, checked: false }));
    };

    populateSection('organisation', extractValues(apiData, 'organization'));
    populateSection('file-format', extractValues(apiData, 'fileFormat'));
    populateSection('organisation-type', extractValues(apiData, 'organizationType'));
    populateSection('model-type', extractValues(apiData, 'modelType'));

    const accessPolicies = extractValues(apiData, 'accessPolicy');
    const permissionMapping: Record<string, string> = {
      open: 'Public',
      restricted: 'Restricted',
      pii: 'Personal',
    };
    if (accessPolicies.length > 0) {
      const section = this.filterConfig.sections.find((s) => s.id === 'permissions');
      if (section) {
        section.options = accessPolicies.map((value) => ({
          id: value,
          label: permissionMapping[value.toLowerCase()] ?? value,
          checked: false,
          originalValue: value,
        }));
      }
    }
  }
}
